import { useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { useForm, useFormState } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import { getGeneralInfoProjectForCouncil } from 'apis/projects.api';

import {
  getSummaryReport,
  createSummaryReportReview,
  updateSummaryReportReview,
} from 'apis/summaryReport.api';
import { TOAST } from 'constant';
import { getRoles, checkIsAllowedCRUDFromContract } from 'utils/func';

import { toast } from 'layout';

import ReviewForm from 'features/SubmitProjectProposal/components/ReviewForm';
import { Can } from 'components/Can';
import CommentModal from 'features/SubmitProjectProposal/components/CommentModal';
import ViewHistoryModal from 'features/SubmitProjectProposal/components/ViewHistoryModal';
import { createSummaryReportReviewSchema } from 'features/SubmitProjectProposal/validation';

export default function MidtermDocumentsChecker({
  projectId: passedProjectId,
  summaryReportId: passedSummaryReportId,
}) {
  const searchParams = useParams();
  const projectId = passedProjectId || searchParams?.projectId;
  const summaryReportId = passedSummaryReportId || searchParams?.summaryReportId;

  const { t } = useTranslation();
  const roles = getRoles();

  const [visible, setVisible] = useState(false);
  const [isViewHistoryVisible, setIsViewHistoryVisible] = useState(false);
  const [requestReviseData, setRequestReviseData] = useState({});

  const {
    handleSubmit,
    control,
    getValues,
    reset,
    formState: { isDirty, dirtyFields },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(createSummaryReportReviewSchema),
  });

  const { errors } = useFormState({ control });
  // // #region call api

  const { data: summaryReportData, refetch } = useQuery(
    ['summaryReport', summaryReportId],
    () => getSummaryReport(summaryReportId),
    {
      enabled: !!summaryReportId,
    }
  );

  const summaryReport = useMemo(() => summaryReportData?.data, [summaryReportData]);

  const summaryReportRevises = useMemo(
    () => summaryReportData?.data?.summaryReport?.summaryReportRevises ?? [],
    [summaryReportData]
  );

  const isRequestRevise = useMemo(
    () => summaryReportRevises?.findIndex((item) => item?.isRevise) !== -1,
    [summaryReportRevises]
  );

  const { data: generalInfoProjectData } = useQuery(
    ['GeneralInfoProject', projectId],
    () => getGeneralInfoProjectForCouncil(projectId),
    {
      staleTime: 1000 * 60 * 5,
      enabled: !!projectId,
    }
  );
  const generalInfoProjectProposal = useMemo(
    () => generalInfoProjectData?.data?.projectProposal,
    [generalInfoProjectData]
  );

  const { mutate: mutateCreateSummaryReportReview, isLoading: isCreateLoading } =
    useMutation(createSummaryReportReview);
  const { mutate: mutateUpdateSummaryReportReview, isLoading: isUpdateLoading } =
    useMutation(updateSummaryReportReview);

  const handleCloseCommentModal = () => {
    setVisible(false);
    setRequestReviseData({});
    reset();
  };

  const onSubmit = async () => {
    const body = {
      comment: getValues('comment'),
      deadline: getValues('deadline') || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    };
    if (requestReviseData?.id) {
      mutateUpdateSummaryReportReview(
        { summaryReportId, reviewId: requestReviseData.id, body },
        {
          onSuccess: () => {
            toast(TOAST.SUCCESS, t('submitDocument.toast.updateSuccess'));
            setVisible(false);
            refetch();
          },
          onError: () => {
            toast(TOAST.ERROR, t('submitDocument.toast.error'));
          },
        }
      );
    } else {
      mutateCreateSummaryReportReview(
        { summaryReportId, body },
        {
          onSuccess: () => {
            toast(TOAST.SUCCESS, t('submitDocument.toast.success'));
            setVisible(false);
            refetch();
          },
          onError: () => {
            toast(TOAST.ERROR, t('submitDocument.toast.error'));
          },
        }
      );
    }
  };

  return (
    <div className="p-5">
      <ReviewForm
        summaryReport={summaryReport}
        projectId={projectId}
        setIsViewHistoryVisible={setIsViewHistoryVisible}
      />
      {checkIsAllowedCRUDFromContract(generalInfoProjectProposal?.projectType, roles) && (
        <Can do="update" on="summary-report">
          <div className="flex justify-content-end mt-4 mr-4 gap-2">
            {summaryReportRevises?.length > 0 && (
              <Button
                type="button"
                className="flex justify-content-center items-center"
                onClick={() => setIsViewHistoryVisible(true)}
                severity="info"
              >
                {t('submitDocument.viewHistory')}
              </Button>
            )}
            {isRequestRevise && (
              <Button
                type="button"
                className="flex justify-content-center items-center"
                onClick={() => {
                  setRequestReviseData(summaryReportRevises?.[summaryReportRevises.length - 1]);
                  setVisible(true);
                }}
                severity="warning"
              >
                {t('submitDocument.permissionUpdateComment')}
              </Button>
            )}
            {!isRequestRevise && summaryReport?.summaryReport?.submittedAt && (
              <Button
                type="button"
                className="flex justify-content-center items-center"
                onClick={() => setVisible(true)}
                tooltip={t('submitDocument.note')}
                tooltipOptions={{
                  position: 'left',
                }}
              >
                {t('submitDocument.permissionUpdateReport')}
              </Button>
            )}

            {!isRequestRevise && !summaryReport?.summaryReport?.submittedAt && (
              <Button
                className="flex justify-content-center items-center cursor-auto"
                severity="secondary"
                tooltip={t('submitDocument.note')}
                tooltipOptions={{ position: 'left' }}
              >
                {t('submitDocument.permissionUpdateReport')}
              </Button>
            )}
          </div>
        </Can>
      )}
      <CommentModal
        visible={visible}
        onClose={handleCloseCommentModal}
        onSubmit={onSubmit}
        handleSubmit={handleSubmit}
        control={control}
        errors={errors}
        data={requestReviseData}
        isSubmitting={
          !(isDirty && Object.keys(dirtyFields).length) || isCreateLoading || isUpdateLoading
        }
      />
      <ViewHistoryModal
        visible={isViewHistoryVisible}
        setVisible={setIsViewHistoryVisible}
        history={summaryReportData?.data?.summaryReport?.summaryReportRevises}
      />
    </div>
  );
}

MidtermDocumentsChecker.propTypes = {
  projectId: PropTypes.string,
  summaryReportId: PropTypes.string,
};

MidtermDocumentsChecker.defaultProps = {
  projectId: '',
  summaryReportId: '',
};
